/**
 * Lead-capture modal — appears 25 seconds into a visitor's landing session
 * IF: they haven't dismissed it, aren't logged in, and analytics is allowed.
 * Ties their email to the visitor cookie for the 24h follow-up.
 */
import React, { useEffect, useState } from "react";
import { X, Mail } from "lucide-react";
import { captureVisitorEmail } from "@/lib/tracker";

const DISMISS_KEY = "careco24.lead_modal.v1";
const DELAY_MS = 25_000;

function alreadyDismissed() {
    try { return !!localStorage.getItem(DISMISS_KEY); } catch { return false; }
}
function markDismissed() {
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch { /* ignore */ }
}

export default function LeadCaptureModal() {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        // Only show on public pages (not /dashboard, /auth, etc.)
        const path = window.location.pathname;
        const isPublic = path === "/" || path.startsWith("/states") || path === "/pricing";
        if (!isPublic) return;
        if (alreadyDismissed()) return;
        // Skip if authenticated (session cookie / JWT presence check via localStorage)
        if (localStorage.getItem("careco24.token")) return;
        const t = setTimeout(() => setOpen(true), DELAY_MS);
        return () => clearTimeout(t);
    }, []);

    function close() {
        markDismissed();
        setOpen(false);
    }

    async function submit(e) {
        e.preventDefault();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
        setBusy(true);
        try {
            await captureVisitorEmail(email, "landing_modal");
            setDone(true);
            markDismissed();
            setTimeout(() => setOpen(false), 2500);
        } finally { setBusy(false); }
    }

    if (!open) return null;
    return (
        <div
            data-testid="lead-capture-modal"
            className="fixed bottom-6 left-6 z-[9997] max-w-sm bg-white border border-stone-200 rounded-2xl shadow-2xl p-5 font-dmsans"
        >
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-emerald-700" />
                    <div className="font-outfit font-semibold text-sm text-stone-900">
                        Get the launch invite
                    </div>
                </div>
                <button
                    data-testid="lead-capture-close"
                    onClick={close}
                    aria-label="Close"
                    className="text-stone-400 hover:text-stone-700"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            {done ? (
                <p className="text-xs text-emerald-700 font-medium">
                    ✓ Got it — we&apos;ll be in touch. Check your inbox.
                </p>
            ) : (
                <>
                    <p className="text-xs text-stone-600 leading-relaxed">
                        Drop your email and we&apos;ll send you the roll-out plan for your state + early access to shift blasts.
                    </p>
                    <form onSubmit={submit} className="mt-3 flex gap-2">
                        <input
                            data-testid="lead-capture-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@work.com"
                            className="flex-1 h-9 rounded-full border border-stone-200 px-3 text-xs outline-none focus:border-emerald-600"
                        />
                        <button
                            data-testid="lead-capture-submit"
                            disabled={busy}
                            className="px-4 h-9 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold"
                        >
                            {busy ? "…" : "Notify me"}
                        </button>
                    </form>
                    <p className="mt-2 text-[10px] text-stone-400">
                        No spam. Unsubscribe anytime with one click.
                    </p>
                </>
            )}
        </div>
    );
}
